import debugx from "debugx";
import { Coordinate } from "../types";

const debug = debugx("bikedash:route-fetch");

interface RouteCache {
  [key: string]: {
    overpassData?: any; // Raw OSM data from Overpass API
    timestamp: number;
    name: string;
    description?: string;
    boundingBox?: number[]; // Store bounding box for the search
  };
}

class RouteFetchService {
  private static readonly CACHE_KEY = "bikedash_route_cache";
  private static readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
  private static readonly OVERPASS_URL =
    "https://bikedash-osm.bitbased.net/api/interpreter";
  private static readonly RATE_LIMIT_DELAY = 1100; // 1.1 seconds to be safe
  private cache: RouteCache = {};
  private lastNominatimRequest = 0;
  private lastOverpassRequest = 0;
  private requestQueue: Array<{
    type: "nominatim" | "overpass";
    resolve: () => void;
  }> = [];
  private isProcessingQueue = false;

  constructor() {
    this.loadCache();
  }

  private loadCache(): void {
    try {
      const stored = localStorage.getItem(RouteFetchService.CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Clean out old entries
        const now = Date.now();
        for (const key in parsed) {
          if (now - parsed[key].timestamp < RouteFetchService.CACHE_DURATION) {
            this.cache[key] = parsed[key];
          }
        }
        debug(
          "Loaded route cache with %d entries",
          Object.keys(this.cache).length,
        );
      }
    } catch (error) {
      debug.error("Failed to load route cache:", error);
    }
  }

  private saveCache(): void {
    try {
      localStorage.setItem(
        RouteFetchService.CACHE_KEY,
        JSON.stringify(this.cache),
      );
    } catch (error) {
      debug.error("Failed to save route cache:", error);
    }
  }

  /**
   * Rate limit API requests to respect 1 req/sec limit
   */
  private async waitForRateLimit(
    apiType: "nominatim" | "overpass",
  ): Promise<void> {
    return new Promise((resolve) => {
      this.requestQueue.push({ type: apiType, resolve });
      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift()!;
      const now = Date.now();

      const lastRequest =
        request.type === "nominatim"
          ? this.lastNominatimRequest
          : this.lastOverpassRequest;

      const timeSinceLastRequest = now - lastRequest;
      const waitTime = Math.max(
        0,
        RouteFetchService.RATE_LIMIT_DELAY - timeSinceLastRequest,
      );

      if (waitTime > 0) {
        debug(`Rate limiting ${request.type}: waiting ${waitTime}ms`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }

      // Update last request time
      if (request.type === "nominatim") {
        this.lastNominatimRequest = Date.now();
      } else {
        this.lastOverpassRequest = Date.now();
      }

      request.resolve();
    }

    this.isProcessingQueue = false;
  }

  /**
   * Fetch a cycling route by name using Nominatim + Overpass
   */
  async fetchRoute(
    routeName: string,
    boundingBox?: { north: number; south: number; east: number; west: number },
    forcePathFinding: boolean = false, // Force use of path-finding algorithm for debugging
  ): Promise<Coordinate[] | null> {
    // Check cache for raw data
    const cacheKey = `${routeName}_${JSON.stringify(boundingBox || {})}`;
    let overpassData = null;
    let searchBbox = null;

    if (this.cache[cacheKey]?.overpassData) {
      debug("Using cached OSM data for:", routeName);
      overpassData = this.cache[cacheKey].overpassData;
      searchBbox = this.cache[cacheKey].boundingBox;
    } else {
      try {
        // First, search for the route using Nominatim
        debug("Searching for route:", routeName);

        const nominatimParams = new URLSearchParams({
          q: routeName,
          format: "json",
          limit: "5",
          addressdetails: "1",
        });

        if (boundingBox) {
          nominatimParams.append(
            "viewbox",
            `${boundingBox.west},${boundingBox.north},${boundingBox.east},${boundingBox.south}`,
          );
          nominatimParams.append("bounded", "1");
        }

        // Rate limit Nominatim request
        await this.waitForRateLimit("nominatim");

        const nominatimResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?${nominatimParams}`,
          {
            headers: {
              "User-Agent": "BikeDash/1.0",
            },
          },
        );

        if (!nominatimResponse.ok) {
          throw new Error("Nominatim search failed");
        }

        const searchResults = await nominatimResponse.json();
        if (searchResults.length === 0) {
          debug("No results found for:", routeName);
          return null;
        }

        // Get the first result's bounding box
        const result = searchResults[0];
        const bbox = result.boundingbox;

        // Build Overpass query to get cycling routes and ways
        // First try to find route relations, then individual ways
        const overpassQuery = `
        [out:json][timeout:25];
        (
          // Try to find bicycle route relations
          relation["route"="bicycle"]["name"~"${routeName}",i](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
          relation["route"="bicycle"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
          // Also get individual cycling ways
          way["highway"="cycleway"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
          way["bicycle"="designated"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
          way["bicycle"="yes"]["highway"~"cycleway|path|track"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
        );
        (._;>;);
        out geom;
      `;

        debug("Fetching route geometry from Overpass");

        // Rate limit Overpass request
        await this.waitForRateLimit("overpass");

        const overpassResponse = await fetch(RouteFetchService.OVERPASS_URL, {
          method: "POST",
          body: `data=${encodeURIComponent(overpassQuery)}`,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        if (!overpassResponse.ok) {
          throw new Error("Overpass query failed");
        }

        overpassData = await overpassResponse.json();
        searchBbox = bbox;

        // Cache the raw OSM data
        this.cache[cacheKey] = {
          overpassData,
          timestamp: Date.now(),
          name: routeName,
          description: result.display_name,
          boundingBox: bbox,
        };
        this.saveCache();
        debug("Cached raw OSM data for:", routeName);
      } catch (error) {
        debug.error("Failed to fetch OSM data:", error);
        return null;
      }
    }

    // Process the OSM data (always recalculate for debugging)
    debug("=== PROCESSING OSM DATA ===");
    const coordinates: Coordinate[] = [];

    if (
      overpassData &&
      overpassData.elements &&
      overpassData.elements.length > 0
    ) {
      // First, look for route relations
      const relations = overpassData.elements.filter(
        (e: any) => e.type === "relation",
      );
      const ways = overpassData.elements.filter((e: any) => e.type === "way");
      const nodes = overpassData.elements.filter((e: any) => e.type === "node");

      debug(
        "Found %d relations, %d ways, %d nodes",
        relations.length,
        ways.length,
        nodes.length,
      );

      // Create a map of nodes for quick lookup
      const nodeMap = new Map();
      nodes.forEach((node: any) => {
        nodeMap.set(node.id, [node.lon, node.lat]);
      });

      // Check if we should force path-finding for debugging
      if ((forcePathFinding || relations.length === 0) && ways.length > 0) {
        if (forcePathFinding) {
          debug("🔧 FORCING PATH-FINDING ALGORITHM (debug mode)");
        }
        // No relations found, try to connect ways manually using proper endpoint tracking
        debug("Processing %d ways without relations", ways.length);

        // Build a graph with endpoint connections
        interface WayInfo {
          id: string;
          start: { lon: number; lat: number };
          end: { lon: number; lat: number };
          geometry: any[];
          length: number;
          tags?: any;
        }

        const wayInfos: WayInfo[] = [];
        const wayMap = new Map<string, WayInfo>();

        // Filter out very small segments (likely connectors) unless they're the only ones
        const MIN_SEGMENT_LENGTH = 5; // Minimum points to be considered a main segment

        for (const way of ways) {
          if (!way.geometry || way.geometry.length < 2) continue;

          const info: WayInfo = {
            id: way.id,
            start: way.geometry[0],
            end: way.geometry[way.geometry.length - 1],
            geometry: way.geometry,
            length: way.geometry.length,
            tags: way.tags,
          };

          wayInfos.push(info);
          wayMap.set(way.id, info);
        }

        // Separate main segments from connectors - but use ALL segments for connectivity
        const mainSegments = wayInfos.filter(
          (w) => w.length >= MIN_SEGMENT_LENGTH,
        );

        debug(
          "Found %d main segments (%d total)",
          mainSegments.length,
          wayInfos.length,
        );

        // Build adjacency graph with bidirectional connections
        interface Connection {
          wayId: string;
          fromEnd: "start" | "end";
          toEnd: "start" | "end";
          distance: number;
        }

        const connections = new Map<
          string,
          Map<"start" | "end", Connection[]>
        >();

        // Initialize connection maps for ALL segments
        for (const way of wayInfos) {
          connections.set(
            way.id,
            new Map([
              ["start", []],
              ["end", []],
            ]),
          );
        }

        // Build connections between ALL segments (including small connectors)
        const checkConnection = (p1: any, p2: any): number => {
          const dist = Math.sqrt(
            Math.pow(p1.lon - p2.lon, 2) + Math.pow(p1.lat - p2.lat, 2),
          );
          return dist;
        };

        let totalConnections = 0;
        for (const way1 of wayInfos) {
          for (const way2 of wayInfos) {
            if (way1.id === way2.id) continue;

            const distStartStart = checkConnection(way1.start, way2.start);
            const distStartEnd = checkConnection(way1.start, way2.end);
            const distEndStart = checkConnection(way1.end, way2.start);
            const distEndEnd = checkConnection(way1.end, way2.end);

            const CONNECT_THRESHOLD = 0.00005; // ~5m - tighter threshold

            if (distStartStart < CONNECT_THRESHOLD) {
              connections.get(way1.id)!.get("start")!.push({
                wayId: way2.id,
                fromEnd: "start",
                toEnd: "start",
                distance: distStartStart,
              });
              totalConnections++;
            }
            if (distStartEnd < CONNECT_THRESHOLD) {
              connections.get(way1.id)!.get("start")!.push({
                wayId: way2.id,
                fromEnd: "start",
                toEnd: "end",
                distance: distStartEnd,
              });
              totalConnections++;
            }
            if (distEndStart < CONNECT_THRESHOLD) {
              connections.get(way1.id)!.get("end")!.push({
                wayId: way2.id,
                fromEnd: "end",
                toEnd: "start",
                distance: distEndStart,
              });
              totalConnections++;
            }
            if (distEndEnd < CONNECT_THRESHOLD) {
              connections.get(way1.id)!.get("end")!.push({
                wayId: way2.id,
                fromEnd: "end",
                toEnd: "end",
                distance: distEndEnd,
              });
              totalConnections++;
            }
          }
        }

        debug("Found %d connections between segments", totalConnections);

        // Log connection summary
        const connectionSummary = Array.from(connections.entries())
          .filter(([_, endMap]) => {
            const startConns = endMap.get("start")!.length;
            const endConns = endMap.get("end")!.length;
            return startConns > 0 || endConns > 0;
          })
          .map(([wayId, endMap]) => ({
            wayId,
            points: wayMap.get(wayId)!.length,
            startConnections: endMap.get("start")!.length,
            endConnections: endMap.get("end")!.length,
          }));

        if (connectionSummary.length > 0) {
          debug(
            "Connection graph: %d segments with connections",
            connectionSummary.length,
            { connections: connectionSummary },
          );
        }

        // Use all segments for pathfinding
        const allSegments = wayInfos;

        // Sort connections by distance for better path finding
        for (const [wayId, endMap] of connections) {
          for (const [end, conns] of endMap) {
            conns.sort((a, b) => a.distance - b.distance);
          }
        }

        // Exhaustive search for best path (feasible with ~10 segments)
        let bestPath: Array<{ wayId: string; reversed: boolean }> = [];
        let bestPathLength = 0;
        let bestIsLoop = false;
        let pathsExplored = 0;
        let loopsFound = 0;

        // Recursive DFS to explore all possible paths
        const explorePath = (
          currentWayId: string,
          currentReversed: boolean,
          visited: Set<string>,
          path: Array<{ wayId: string; reversed: boolean }>,
          totalLength: number,
          startWayId: string,
          startEnd: "start" | "end",
          depth: number = 0,
        ): void => {
          pathsExplored++;

          // Log current exploration state
          if (depth <= 2) {
            const indent = "  ".repeat(depth);
            debug(
              "%sExploring from way %s (%s), path length: %d, total points: %d",
              indent,
              currentWayId,
              currentReversed ? "reversed" : "forward",
              path.length,
              totalLength,
            );
          }

          // Check if we've formed a loop back to start
          if (path.length > 2) {
            const currentEnd = currentReversed ? "start" : "end";
            const currentConnections =
              connections.get(currentWayId)?.get(currentEnd) || [];

            // Check if we can connect back to the start
            const canLoop = currentConnections.some(
              (conn) => conn.wayId === startWayId && conn.toEnd === startEnd,
            );

            if (canLoop) {
              loopsFound++;
              debug("  -> Can form loop with %d segments", path.length, {
                path: path.map((p) => ({
                  wayId: p.wayId,
                  reversed: p.reversed,
                })),
              });

              if (totalLength > bestPathLength) {
                bestPath = [...path];
                bestPathLength = totalLength;
                bestIsLoop = true;
                debug(
                  "  ✓ NEW BEST LOOP: %d segments, %d points",
                  path.length,
                  totalLength,
                );
                return; // Prefer loops, so stop searching once we find a good one
              }
            }
          }

          // Update best path if this is longer (for non-loops)
          if (!bestIsLoop && totalLength > bestPathLength) {
            bestPath = [...path];
            bestPathLength = totalLength;
            debug(
              "  -> New best linear path: %d segments, %d points",
              path.length,
              totalLength,
            );
          }

          // Explore all connections from current segment
          const currentEnd = currentReversed ? "start" : "end";
          const possibleConnections =
            connections.get(currentWayId)?.get(currentEnd) || [];

          if (depth <= 3 && possibleConnections.length > 0) {
            debug(
              "    From %s's %s: %d possible connections",
              currentWayId,
              currentEnd,
              possibleConnections.length,
            );
          }

          for (const conn of possibleConnections) {
            if (visited.has(conn.wayId)) {
              if (depth <= 3) {
                debug("      - Skipping %s (already visited)", conn.wayId);
              }
              continue;
            }

            const nextWay = wayMap.get(conn.wayId);
            if (!nextWay) continue;

            // Skip very small segments unless they're the only way to continue
            if (nextWay.length < 3 && possibleConnections.length > 1) {
              const otherOptions = possibleConnections.filter((c) => {
                const w = wayMap.get(c.wayId);
                return w && w.length >= 3 && !visited.has(c.wayId);
              });
              if (otherOptions.length > 0) {
                if (depth <= 3) {
                  debug(
                    "      - Skipping small segment %s (%d pts)",
                    conn.wayId,
                    nextWay.length,
                  );
                }
                continue;
              }
            }

            const newVisited = new Set(visited);
            newVisited.add(conn.wayId);

            const nextReversed = conn.toEnd === "end";
            const newPath = [
              ...path,
              { wayId: conn.wayId, reversed: nextReversed },
            ];

            if (depth <= 3) {
              debug(
                "      + Exploring %s (%d pts, %s)",
                conn.wayId,
                nextWay.length,
                nextReversed ? "reversed" : "forward",
              );
            }

            explorePath(
              conn.wayId,
              nextReversed,
              newVisited,
              newPath,
              totalLength + nextWay.length,
              startWayId,
              startEnd,
              depth + 1,
            );

            // Early exit if we found a good loop
            if (
              bestIsLoop &&
              bestPathLength >
                allSegments.reduce((sum, w) => sum + w.length, 0) * 0.8
            ) {
              debug("Early exit: Found sufficiently good loop");
              return;
            }
          }
        };

        // Try starting from each segment and direction
        debug(
          "Starting exhaustive search with %d segments",
          allSegments.length,
        );
        const segmentDetails = allSegments.map((w) => ({
          id: w.id,
          points: w.length,
        }));
        debug(
          "Segment details: %d segments, %d total points",
          allSegments.length,
          allSegments.reduce((sum, w) => sum + w.length, 0),
          { segments: segmentDetails },
        );

        let startPointsTried = 0;
        for (const startWay of allSegments) {
          // Skip very small segments as starting points if we have better options
          if (startWay.length < 3 && allSegments.some((w) => w.length >= 5)) {
            debug(
              "Skipping small starting segment %s (%d pts)",
              startWay.id,
              startWay.length,
            );
            continue;
          }

          for (const startReversed of [false, true]) {
            startPointsTried++;
            debug(
              "=== Starting point %d: Way %s (%d pts, %s) ===",
              startPointsTried,
              startWay.id,
              startWay.length,
              startReversed ? "reversed" : "forward",
            );

            const visited = new Set<string>([startWay.id]);
            const initialPath = [
              { wayId: startWay.id, reversed: startReversed },
            ];
            const startEnd = startReversed ? "end" : "start";

            explorePath(
              startWay.id,
              startReversed,
              visited,
              initialPath,
              startWay.length,
              startWay.id,
              startEnd,
            );

            // Stop if we found a good loop
            if (
              bestIsLoop &&
              bestPathLength >
                allSegments.reduce((sum, w) => sum + w.length, 0) * 0.8
            ) {
              const percentage = Math.round(
                (bestPathLength /
                  allSegments.reduce((sum, w) => sum + w.length, 0)) *
                  100,
              );
              debug(
                "Stopping search: Found good loop covering %d%% of segments",
                percentage,
              );
              break;
            }
          }

          if (
            bestIsLoop &&
            bestPathLength >
              allSegments.reduce((sum, w) => sum + w.length, 0) * 0.8
          ) {
            break;
          }
        }

        debug("=== SEARCH COMPLETE ===");
        debug("Search statistics", {
          pathsExplored,
          loopsFound,
          resultType: bestIsLoop ? "LOOP" : "LINEAR PATH",
          bestPathSegments: bestPath.length,
          bestPathPoints: bestPathLength,
        });

        if (bestPath.length > 0) {
          const pathDetails = bestPath.map((p) => ({
            wayId: p.wayId,
            reversed: p.reversed,
            points: wayMap.get(p.wayId)?.length || 0,
          }));
          debug(
            "Best path found: %d segments forming %s",
            bestPath.length,
            bestIsLoop ? "loop" : "linear path",
            { path: pathDetails },
          );
        }

        // Build coordinates from the best path
        if (bestPath.length > 0) {
          debug(
            "Building coordinates from best path: %d segments, %d points",
            bestPath.length,
            bestPathLength,
          );
          const orderedCoords: Coordinate[] = [];
          let lastCoord: Coordinate | null = null;

          for (let i = 0; i < bestPath.length; i++) {
            const segment = bestPath[i];
            const way = wayMap.get(segment.wayId);
            if (!way) continue;

            let wayCoords = way.geometry.map(
              (n: any) => [n.lon, n.lat] as Coordinate,
            );

            if (segment.reversed) {
              wayCoords.reverse();
            }

            // Add coordinates, avoiding duplicates at connection points
            for (let j = 0; j < wayCoords.length; j++) {
              const coord = wayCoords[j];

              // Skip duplicate connection points
              if (lastCoord && j === 0) {
                const dist = Math.sqrt(
                  Math.pow(coord[0] - lastCoord[0], 2) +
                    Math.pow(coord[1] - lastCoord[1], 2),
                );

                if (dist < 0.00001) {
                  continue; // Skip duplicate
                } else if (dist > 0.001) {
                  debug.warn(
                    "Gap of %f between segments %d and %d",
                    dist,
                    i - 1,
                    i,
                  );
                }
              }

              orderedCoords.push(coord);
              lastCoord = coord;
            }
          }

          // Close the loop if we found one
          if (bestIsLoop && orderedCoords.length > 2) {
            const first = orderedCoords[0];
            const last = orderedCoords[orderedCoords.length - 1];
            const dist = Math.sqrt(
              Math.pow(first[0] - last[0], 2) + Math.pow(first[1] - last[1], 2),
            );

            if (dist > 0.00001 && dist < 0.001) {
              orderedCoords.push([first[0], first[1]]);
              debug("Closed loop");
            }
          }

          coordinates.push(...orderedCoords);
        } else if (allSegments.length > 0) {
          // Fallback: use the longest single segment
          debug("No connected path found, using longest individual segment");
          const longestWay = allSegments.reduce((longest, way) =>
            way.length > longest.length ? way : longest,
          );

          coordinates.push(
            ...longestWay.geometry.map(
              (n: any) => [n.lon, n.lat] as Coordinate,
            ),
          );
        }
      } else if (relations.length > 0 && !forcePathFinding) {
        // Process route relation members to get ordered ways
        debug(
          "Processing route relation (using first of %d)",
          relations.length,
        );
        const relation = relations[0]; // Use the first relation
        debug(
          "Relation: %s, %d members",
          relation.tags?.name || "unnamed",
          relation.members?.length || 0,
        );

        // Build ordered list of ways from relation members
        const wayIds = new Set();
        if (relation.members) {
          relation.members
            .filter((m: any) => m.type === "way")
            .forEach((m: any) => wayIds.add(m.ref));
        }

        // Process ways in order
        let waysProcessed = 0;
        ways.forEach((way: any) => {
          if (wayIds.has(way.id) || ways.length === 1) {
            waysProcessed++;
            if (way.geometry) {
              way.geometry.forEach((node: any) => {
                coordinates.push([node.lon, node.lat]);
              });
            } else if (way.nodes) {
              way.nodes.forEach((nodeId: any) => {
                const coord = nodeMap.get(nodeId);
                if (coord) coordinates.push(coord);
              });
            }
          }
        });
        debug(
          "Processed %d ways from relation, got %d coordinates",
          waysProcessed,
          coordinates.length,
        );
      }

      // If we have a loop, ensure it's closed
      if (coordinates.length > 2) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        const dist = Math.sqrt(
          Math.pow(first[0] - last[0], 2) + Math.pow(first[1] - last[1], 2),
        );

        // If start and end are close but not identical, close the loop
        if (dist < 0.001 && dist > 0) {
          coordinates.push([first[0], first[1]]);
        }
      }
    }

    if (coordinates.length > 0) {
      debug("Processed %d coordinates for: %s", coordinates.length, routeName);
    } else {
      debug("No coordinates found for: %s", routeName);
    }

    return coordinates;
  }

  /**
   * Fetch simple waypoints for a route (fallback for when full route isn't available)
   */
  async fetchWaypoints(
    routeName: string,
    count: number = 10,
  ): Promise<Coordinate[] | null> {
    const cacheKey = `waypoints_${routeName}_${count}`;

    // Note: For waypoints, we'll keep the simple caching as-is since
    // these don't go through the complex path-finding algorithm
    // and are just simple geometric points
    if (this.cache[cacheKey]?.overpassData) {
      debug("Using cached waypoint data for:", routeName);
      const data = this.cache[cacheKey].overpassData;
      return data.coordinates || null;
    }

    try {
      debug("Fetching waypoints for:", routeName);

      const params = new URLSearchParams({
        q: routeName,
        format: "json",
        limit: "1",
        polygon_geojson: "1",
      });

      // Rate limit Nominatim request
      await this.waitForRateLimit("nominatim");

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            "User-Agent": "BikeDash/1.0",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Nominatim search failed");
      }

      const results = await response.json();
      if (results.length === 0) {
        return null;
      }

      const result = results[0];
      const coordinates: Coordinate[] = [];

      // If we have a polygon, sample points from it
      if (result.geojson && result.geojson.coordinates) {
        const geom = result.geojson;
        if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
          const ring =
            geom.type === "Polygon"
              ? geom.coordinates[0]
              : geom.coordinates[0][0];

          // Sample evenly spaced points from the polygon
          const step = Math.floor(ring.length / count);
          for (
            let i = 0;
            i < ring.length && coordinates.length < count;
            i += step
          ) {
            coordinates.push([ring[i][0], ring[i][1]]);
          }
        } else if (geom.type === "LineString") {
          // Use the line string coordinates directly
          for (const coord of geom.coordinates) {
            coordinates.push([coord[0], coord[1]]);
          }
        }
      }

      // If no polygon, create a simple box around the center point
      if (coordinates.length === 0) {
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        const radius = 0.01; // About 1km

        coordinates.push(
          [lon, lat],
          [lon + radius, lat],
          [lon + radius, lat - radius],
          [lon, lat - radius],
          [lon - radius, lat - radius],
          [lon - radius, lat],
          [lon - radius, lat + radius],
          [lon, lat + radius],
          [lon, lat],
        );
      }

      // Cache the waypoint data
      this.cache[cacheKey] = {
        overpassData: { coordinates }, // Store in same format for consistency
        timestamp: Date.now(),
        name: routeName,
        description: result.display_name,
      };
      this.saveCache();

      return coordinates;
    } catch (error) {
      debug.error("Failed to fetch waypoints:", error);
      return null;
    }
  }

  /**
   * Get hardcoded fallback routes for Austin
   */
  getFallbackRoutes(): { [key: string]: Coordinate[] } {
    return {
      veloway: [
        // Simple rectangle in South Austin where Veloway is located
        [-97.8614, 30.1822],
        [-97.855, 30.1822],
        [-97.855, 30.175],
        [-97.8614, 30.175],
        [-97.8614, 30.1822],
      ],
      "lady-bird-lake": [
        // Simple points around the lake
        [-97.7505, 30.2624], // Mopac bridge
        [-97.7431, 30.258], // South shore
        [-97.728, 30.256], // East side
        [-97.726, 30.263], // Northeast
        [-97.743, 30.271], // North shore
        [-97.755, 30.266], // Northwest
        [-97.7505, 30.2624], // Back to start
      ],
      "downtown-austin": [
        // Simple loop downtown
        [-97.7431, 30.2672], // City Hall
        [-97.74, 30.275], // Capitol
        [-97.747, 30.275], // East of Capitol
        [-97.747, 30.267], // Back south
        [-97.7431, 30.2672], // City Hall
      ],
    };
  }

  clearCache(): void {
    this.cache = {};
    localStorage.removeItem(RouteFetchService.CACHE_KEY);
    debug("Cleared route cache");
  }
}

// Singleton instance
export const routeFetchService = new RouteFetchService();
